import * as React from 'react'

import { Alert, Box, Link, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import DimensionsAlertModal from 'modules/finance/components/Dimensions/DimensionsAlertModal'
import EmployeeDimensionsList from 'modules/finance/components/Dimensions/EmployeeDimensionsList'
import SetAssignmentsPanel from 'modules/finance/components/Dimensions/SetAssignmentsPanel'
import Loading from 'modules/finance/components/Loading'
import useEmployeeCostCentersAssignments from 'modules/finance/hooks/useEmployeeCostCentersAssignments'
import type { MutipleValueDimensionAssignment } from 'modules/finance/hooks/useEmployeeCostCentersAssignments'
import useTrackEmployeeChanges from 'modules/finance/hooks/useTrackEmployeeChanges'

interface Props {
  employeeId: number
  employeeFullName: string
  legalEntityId: number
  managerId: number | null
  locationId: number | null
  teamIdsSignature?: string
}

const EmployeeCostCenters = ({
  employeeId,
  employeeFullName,
  legalEntityId,
  managerId,
  locationId,
  teamIdsSignature = '',
}: Props) => {
  const {
    isLoading,
    costCentersManageCostCenters,
    costCentersWithAssignments,
    thereAreCostCenters,
    saveCostCentersAssignments,
    costCenterMembershipsSeeCostCenterMemberships,
    refetch,
  } = useEmployeeCostCentersAssignments({ employeeId, legalEntityId })

  const hasAssignments = costCentersWithAssignments.some(
    (d) => d.type == 'multiple-value' && d.assignments.length > 0
  )

  const { navigation } = useNavigation()
  const [manageCostCentersSidepanelVisible, setManageCostCentersSidepanelVisible] =
    React.useState(false)
  const [costCentersChangedAlertModalVisible, setCostCentersChangedAlertModalVisible] =
    React.useState(false)
  const [alertDescription, setAlertDescription] = React.useState('')

  const showAlert = (description: string) => {
    setAlertDescription(description)
    setCostCentersChangedAlertModalVisible(true)
  }

  const onClickCostCentersModal = () => {
    setManageCostCentersSidepanelVisible(true)
  }

  const onCloseManageCostCentersSidepanel = () => {
    setManageCostCentersSidepanelVisible(false)
  }

  const onCloseCostCentersAlertModal = () => {
    setCostCentersChangedAlertModalVisible(false)
  }

  const handleOnSubmitAlertModal = () => {
    setCostCentersChangedAlertModalVisible(false)
    setManageCostCentersSidepanelVisible(true)
  }

  useTrackEmployeeChanges({
    employeeId,
    managerId,
    locationId,
    teamIdsSignature,
    hasAssignments: hasAssignments && thereAreCostCenters,
    onChange: showAlert,
  })

  return (
    <Loading loading={isLoading}>
      {costCenterMembershipsSeeCostCenterMemberships && (
        <>
          <Section
            illustration="money-bag"
            title={i18n.t('finance.employee_cost_centers_info.title')}
            description={i18n.t('finance.employee_cost_centers_info.description')}
            link={{
              href: navigation.settings.finance.costCenters.fullPath(),
              label: i18n.t('finance.employee_cost_centers_info.go_to'),
            }}
            action={
              thereAreCostCenters
                ? {
                    label: i18n.t('finance.employee_cost_centers_info.edit_assignments'),
                    onClick: onClickCostCentersModal,
                  }
                : undefined
            }
          >
            <Box paddingLeft="s64">
              {thereAreCostCenters ? (
                <EmployeeDimensionsList dimensions={costCentersWithAssignments} />
              ) : (
                <Alert color="warning">
                  <Box flexDirection="column" gap="s16">
                    <Text.Normal size="300" weight="medium">
                      {i18n.t('finance.employee_cost_centers_info.tip.title')}
                    </Text.Normal>
                    {costCentersManageCostCenters && (
                      <Link.Router to={navigation.settings.finance.costCenters.fullPath()}>
                        {i18n.t('finance.employee_cost_centers_info.tip.link')}
                      </Link.Router>
                    )}
                  </Box>
                </Alert>
              )}
            </Box>
          </Section>
          <SetAssignmentsPanel
            employeeId={employeeId}
            employeeFullName={employeeFullName}
            legalEntityId={legalEntityId}
            open={manageCostCentersSidepanelVisible}
            dimensionsAssignments={costCentersWithAssignments}
            saveAssignment={(id: number, dim: MutipleValueDimensionAssignment) =>
              saveCostCentersAssignments(id, dim)
            }
            onClose={onCloseManageCostCentersSidepanel}
            refetch={refetch}
          />
          {costCentersChangedAlertModalVisible && (
            <DimensionsAlertModal
              description={alertDescription}
              onSubmit={handleOnSubmitAlertModal}
              onCloseDimensionsAlertModal={onCloseCostCentersAlertModal}
            />
          )}
        </>
      )}
    </Loading>
  )
}

export default observer(EmployeeCostCenters)
